import * as zod from 'zod';

import { InspectClass } from '@sb/ai/types';
import { Step } from '@sb/remote-control/types';
import { CaptureConfig } from '@sb/routine-runner/Step/Capture/captureConfig';

export namespace RunInspectSkillStep {
  export const name = 'Inspect';
  export const description = 'Count defects via vision';
  export const librarySection = Step.LibrarySection.Vision;
  export const argumentKind = 'RunInspectSkill';

  export const Arguments = zod.object({
    argumentKind: zod.literal(argumentKind),
    skillId: zod.string().default(''),
    /**
     * The skill's defect classes, copied here when the operator picks the
     * skill. A cache for the variable picker, never an editable field.
     *
     * `getStepVariableInfo` is a synchronous switch on the step's own args and
     * is given nothing else, so without this copy there is no way to list
     * `count_<id>` in an `If` before the step has ever run. Threading a live
     * skill lookup through instead would have to be optional — several of its
     * downstream call sites are plain functions that cannot call a hook — and
     * an Inspect step's counts would then appear in the routine editor and be
     * missing from the dashboard widgets.
     *
     * The runner never sees this. It resolves the skill itself, so the skill
     * stays the only source of truth at run time and this copy can only ever
     * mislead the editor, which the step's validation rule reconciles before
     * play. `label` rides along so the picker can show "Reject - Wrinkle" while
     * the variable reads count_Reject_Wrinkle.
     *
     * Picked from `InspectClass` rather than restated, so the cached `id` keeps
     * the `/^\w+$/` constraint that makes it a usable routine-variable key.
     */
    classes: InspectClass.pick({ id: true, label: true }).array().default([]),
    /**
     * Where the inspected frame and its defect record are written.
     *
     * Declared here as well as on the runner's own arguments, and forwarded
     * below, because a zod object strips what it does not declare: an argument
     * the runner accepts but this schema omits is parsed away before
     * `toRoutineRunner` sees it, and the operator gets a capture that saves
     * nothing without any error.
     */
    captureConfig: CaptureConfig.optional(),
  });

  export type Arguments = zod.infer<typeof Arguments>;

  export const toRoutineRunner: Step.ToRoutineRunner = ({
    stepConfiguration: { args },
    stepData,
  }) => {
    if (args?.argumentKind !== argumentKind) {
      throw new TypeError(`Expected argument kind ${argumentKind}`);
    }

    return {
      ...stepData,
      stepKind: 'RunInspectSkill',
      args: {
        skillId: args.skillId,
        captureConfig: args.captureConfig,
      },
    };
  };
}

RunInspectSkillStep satisfies Step.StepKindInfo;
